/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { format, subDays } from 'date-fns';
import { useListChampionshipsQuery } from 'modules/tournaments/store/tournaments/championships-api';

interface AnalyticsFiltersProps {
  tournamentId: number;
  onFiltersChange: (filters: {
    championshipId?: number | null;
    startDate: string;
    endDate: string;
  }) => void;
}

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 8px;
  color: #d1d5db;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: #6ee7b7;
  }

  option {
    background: #1f2937;
  }
`;

const DateInput = styled.input`
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 8px;
  color: #d1d5db;
  padding: 8px 12px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #6ee7b7;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.7);
    cursor: pointer;
  }
`;

const Label = styled.label`
  color: #9ca3af;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AnalyticsFilters = React.memo(({ tournamentId, onFiltersChange }: AnalyticsFiltersProps) => {
  const [selectedChampionshipId, setSelectedChampionshipId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data: championships } = useListChampionshipsQuery({
    tournamentId,
    limit: 100,
    offset: 0,
  });

  useEffect(() => {
    onFiltersChange({ championshipId: selectedChampionshipId, startDate, endDate });
  }, [selectedChampionshipId, startDate, endDate, onFiltersChange]);

  return (
    <FiltersRow>
      <Label>
        Чемпионат
        <Select
          value={selectedChampionshipId ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedChampionshipId(val === '' ? null : Number(val));
          }}
        >
          <option value="">Все чемпионаты</option>
          {championships?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Label>
      <Label>
        С
        <DateInput
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </Label>
      <Label>
        По
        <DateInput
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Label>
    </FiltersRow>
  );
});

export default AnalyticsFilters;
